import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { ACTION_KINDS, type ActionKind } from '@pa/core';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { LiveMark } from '../components/ui/LiveMark';
import { api, type ConnectionView, type Provider } from '../lib/api';
import { useSession } from '../lib/session';

const PROVIDERS: readonly Provider[] = ['google', 'microsoft'];

/** Callback error codes the API redirects with (see M4 contract). */
const ERROR_CODES = ['bad_state', 'denied', 'exchange_failed', 'no_email', 'provider_error'];

/**
 * You → What TWO can access: one row per provider with the account email, the status, the
 * connect / reconnect / disconnect actions (the disconnect confirmation is an inline line, never
 * a modal) and the autonomy badge per action class.
 */
export function ConnectionsScreen() {
  const { t } = useTranslation();
  const { refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const [connections, setConnections] = useState<ConnectionView[] | null>(null);
  const [reload, setReload] = useState(0);
  // ?connected=<provider> / ?error=<code> from the OAuth callback: one quiet line, read once.
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(() => {
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected && PROVIDERS.includes(connected as Provider)) {
      return {
        tone: 'ok',
        text: t('connections.connected', { provider: t(`connections.provider.${connected}`) }),
      };
    }
    if (error) {
      const code = ERROR_CODES.includes(error) ? error : 'provider_error';
      return { tone: 'error', text: t(`connections.errors.${code}`) };
    }
    return null;
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .connections()
      .then((res) => {
        if (!cancelled) setConnections(res.connections);
      })
      .catch(() => {
        if (!cancelled) setConnections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  // After the callback: the session's connection count changes, and the query is cleaned.
  useEffect(() => {
    if (!params.has('connected') && !params.has('error')) return;
    if (params.has('connected')) void refresh();
    const next = new URLSearchParams(params);
    next.delete('connected');
    next.delete('error');
    setParams(next, { replace: true });
  }, [params, setParams, refresh]);

  const start = async (provider: Provider) => {
    setBusy(provider);
    try {
      const { url } = await api.startConnection(provider);
      window.location.assign(url);
    } catch {
      setNotice({ tone: 'error', text: t('connections.errors.provider_error') });
      setBusy(null);
    }
  };

  const disconnect = async (c: ConnectionView) => {
    setBusy(c.connection_id);
    try {
      await api.disconnectConnection(c.connection_id);
      setConfirming(null);
      setNotice({
        tone: 'ok',
        text: t('connections.disconnected', { provider: t(`connections.provider.${c.provider}`) }),
      });
      setReload((n) => n + 1);
      void refresh();
    } catch {
      setNotice({ tone: 'error', text: t('common.error') });
    } finally {
      setBusy(null);
    }
  };

  if (connections === null) {
    return (
      <div className="flex items-center gap-3 py-4" role="status">
        <LiveMark state="thinking" size={16} title={t('common.loading')} />
      </div>
    );
  }

  // The live row per provider: a connected or needs_reconnect row wins over a disconnected one.
  const rowFor = (provider: Provider): ConnectionView | undefined => {
    const rows = connections.filter((c) => c.provider === provider);
    return rows.find((c) => c.status !== 'disconnected') ?? rows[0];
  };
  const anyConnected = connections.some((c) => c.status === 'connected');

  return (
    <div className="mt-4 flex flex-col gap-4">
      {notice ? (
        <p
          role="status"
          className={[
            'text-sm',
            notice.tone === 'error' ? 'text-critical-text' : 'text-success-text',
          ].join(' ')}
          dir="auto"
        >
          {notice.text}
        </p>
      ) : null}

      <ul className="flex flex-col gap-3">
        {PROVIDERS.map((provider) => {
          const c = rowFor(provider);
          const status = c?.status ?? 'disconnected';
          const providerName = t(`connections.provider.${provider}`);
          return (
            <li
              key={provider}
              className="rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-medium text-ink">{providerName}</p>
                  {c && status !== 'disconnected' ? (
                    <p className="truncate text-sm text-ink-2" dir="ltr">
                      {c.account_email}
                    </p>
                  ) : null}
                  <p
                    className={[
                      'mt-0.5 text-xs',
                      status === 'connected' ? 'text-success-text' : 'text-ink-3',
                    ].join(' ')}
                  >
                    {t(`connections.status.${status}`)}
                  </p>
                </div>
                {status === 'connected' && c ? (
                  confirming === c.connection_id ? null : (
                    <Button
                      variant="secondary"
                      disabled={busy !== null}
                      onClick={() => setConfirming(c.connection_id)}
                    >
                      {t('connections.disconnect')}
                    </Button>
                  )
                ) : (
                  <Button
                    loading={busy === provider}
                    disabled={busy !== null && busy !== provider}
                    onClick={() => void start(provider)}
                  >
                    {status === 'needs_reconnect'
                      ? t('connections.reconnect')
                      : t('connections.connect')}
                  </Button>
                )}
              </div>

              {status === 'needs_reconnect' ? (
                <p className="mt-2 text-sm text-attention-text" dir="auto">
                  {t('connections.needsReconnect', { provider: providerName })}
                </p>
              ) : null}
              {c && status === 'connected' && c.mail_available === false ? (
                <p className="mt-2 text-sm text-ink-2" dir="auto" role="note">
                  {t('connections.mailUnavailable')}
                </p>
              ) : null}
              {c && status === 'connected' && c.needs_reconsent === 'mail_draft_scope' ? (
                <div className="mt-2 flex flex-col items-start gap-2" data-needs-reconsent>
                  <p className="text-sm text-attention-text" dir="auto" role="note">
                    {t('connections.reconnectAction.reason.mail_draft_scope')}
                  </p>
                  <Button
                    variant="secondary"
                    loading={busy === provider}
                    disabled={busy !== null && busy !== provider}
                    onClick={() => void start(provider)}
                  >
                    {t(`connections.reconnectAction.${provider}`)}
                  </Button>
                </div>
              ) : null}

              {c && confirming === c.connection_id ? (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
                  <p className="flex-1 text-sm text-ink" dir="auto">
                    {t('connections.confirmDisconnect', { provider: providerName })}
                  </p>
                  <Button
                    variant="critical"
                    loading={busy === c.connection_id}
                    onClick={() => void disconnect(c)}
                  >
                    {t('connections.disconnect')}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={busy !== null}
                    onClick={() => setConfirming(null)}
                  >
                    {t('connections.keep')}
                  </Button>
                </div>
              ) : null}

              {c && status === 'connected' ? (
                <ul className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
                  {ACTION_KINDS.map((action: ActionKind) => {
                    const p = c.permissions.find((x) => x.action === action);
                    const greyed = c.mail_available === false && action.startsWith('mail');
                    return (
                      <li key={action} aria-disabled={greyed || undefined}>
                        <Link
                          to="/you/permissions"
                          className={[
                            'flex min-h-9 items-center gap-1.5 rounded-md text-xs text-ink-2 hover:text-ink',
                            greyed ? 'opacity-50 line-through' : '',
                          ].join(' ')}
                        >
                          <span>{t(`permissions.actions.${action}`)}</span>
                          <Badge level={p?.level ?? 'observe'} />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>

      {!anyConnected ? (
        <div
          role="status"
          className="rounded-lg border border-line bg-surface p-6 text-center text-sm text-ink-2 dark:bg-elevated"
        >
          {t('settings.empty.connections')}
        </div>
      ) : null}
    </div>
  );
}
